import {Component, ElementRef, EventEmitter, forwardRef, Input, Output, Renderer2, SimpleChanges} from '@angular/core';
import {KeyValuePipe, NgForOf, NgIf} from "@angular/common";
import {ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, Validators} from "@angular/forms";

@Component({
  selector: 'app-filter-combobox-multiselect',
  standalone: true,
  imports: [
    NgForOf,
    NgIf,
    KeyValuePipe
  ],
  templateUrl: './filter-combobox-multiselect.component.html',
  styleUrl: './filter-combobox-multiselect.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FilterComboboxMultiselectComponent),
      multi: true
    }
  ]
})
export class FilterComboboxMultiselectComponent implements ControlValueAccessor {

  @Input() label: string = 'Select options';
  @Input() options: Record<string, any>[] = [];
  @Input() required: boolean = false;
  @Input() placeholder: string = 'Select options';
  @Input() displayKey: string = 'name';  // The key to display (and store) in the component
  @Input() valueKey: string = 'id';     // Only used for matching/comparison
  @Input() parentKey: string = 'parentName'; // Key for the parent/group name
  @Input() maxSelection: number = Infinity;  // Default to no limit
  @Input() formControl: FormControl = new FormControl([]);
  @Output() selectionChange = new EventEmitter<any[]>();

  filteredOptions: Record<string, any>[] = [];
  selectedOptions: Record<string, any>[] = [];
  groupedOptions: Record<string, Record<string, any>[]> = {};
  isOpen = false;
  searchTerm = '';
  private clickListener: () => void;

  // To prevent infinite loops
  private isInternalChange = false;

  // ControlValueAccessor implementation
  private onChange: any = () => {};
  private onTouched: any = () => {};
  disabled = false;

  get hasGroupedOptions(): boolean {
    return Object.keys(this.groupedOptions).length > 0;
  }

  get hasMultipleGroups(): boolean {
    return Object.keys(this.groupedOptions).length > 1;
  }

  constructor(private elRef: ElementRef, private renderer: Renderer2) {}

  // ControlValueAccessor methods
  writeValue(value: any): void {
    if (this.isInternalChange) return;

    this.isInternalChange = true;
    try {
      if (value) {
        // If value is provided, update the component's internal state
        const selectedNames = Array.isArray(value) ? value : [];
        this.selectedOptions = this.options.filter(option =>
          selectedNames.includes(option[this.displayKey])
        );
      } else {
        // If no value, reset selections
        this.selectedOptions = [];
      }
    } finally {
      this.isInternalChange = false;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  ngOnInit(): void {
    this.initializeOptions();

    if (this.required && this.formControl) {
      this.formControl.setValidators(Validators.required);
      this.formControl.updateValueAndValidity();
    }

    // Subscribe to changes from the external form control
    if (this.formControl) {
      this.formControl.valueChanges.subscribe(value => {
        if (!this.isInternalChange) {
          this.writeValue(value);
        }
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options'] && changes['options'].currentValue) {
      this.initializeOptions();  // Reinitialize options when input changes
    }
  }

  ngOnDestroy() {
    this.removeClickOutsideListener();
  }

  initializeOptions(): void {
    if (Array.isArray(this.options) && this.options.length > 0) {
      this.filteredOptions = [...this.options];
      this.groupOptions();
    } else {
      console.error('ComboboxMultiselectComponent: options array is undefined or empty.', this.options);
      this.filteredOptions = [];
      this.groupedOptions = {};
    }

    // Initialize selectedOptions based on form control value (which now stores names)
    if (this.formControl && this.formControl.value) {
      const selectedNames = Array.isArray(this.formControl.value) ? this.formControl.value : [];
      this.selectedOptions = this.options.filter(option =>
        selectedNames.includes(option[this.displayKey])
      );
    } else {
      this.selectedOptions = [];
    }
  }

  // Group options by parent name
  groupOptions(): void {
    this.groupedOptions = {};

    this.filteredOptions.forEach(option => {
      const parentName = option[this.parentKey] || 'Other';

      if (!this.groupedOptions[parentName]) {
        this.groupedOptions[parentName] = [];
      }

      this.groupedOptions[parentName].push(option);
    });
  }

  // Chevron button: the only control that toggles (open <-> close).
  toggleDropdown(event: MouseEvent): void {
    if (this.disabled) return;

    event.stopPropagation();
    if (this.isOpen) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }

    // Mark as touched
    this.onTouched();
  }

  // Text input: clicking only ever opens — never closes — so clicking the
  // field again (to refocus or move the caret) can't hide the list mid-search.
  onInputClick(event: MouseEvent): void {
    if (this.disabled) return;

    event.stopPropagation();
    this.openDropdown();
    this.onTouched();
  }

  onInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.isOpen) {
      event.stopPropagation();
      this.closeDropdown();
    } else if (event.key === 'ArrowDown' && !this.isOpen) {
      event.preventDefault();
      this.openDropdown();
    }
  }

  onSearchTermChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchTerm = input.value;
    this.filterOptions();
    // Typing IS searching: always show the results while the user types,
    // even if the list was closed (e.g. via Escape or an accidental toggle).
    this.openDropdown();
  }

  private openDropdown(): void {
    if (this.disabled || this.isOpen) return;
    this.isOpen = true;
    this.addClickOutsideListener();
  }

  private closeDropdown(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.removeClickOutsideListener();
  }

  filterOptions(): void {
    // Filter by both name and parent name
    this.filteredOptions = this.options.filter(option =>
      option[this.displayKey]?.toString().toLowerCase().includes(this.searchTerm.toLowerCase()) ||
      option[this.parentKey]?.toString().toLowerCase().includes(this.searchTerm.toLowerCase())
    );

    // Regroup the filtered options
    this.groupOptions();
  }

  toggleSelection(option: Record<string, any>): void {
    if (this.disabled || option[this.valueKey] === null) {
      return; // Do not select if disabled or "Data not found." placeholder
    }

    const index = this.selectedOptions.findIndex(selected => selected[this.valueKey] === option[this.valueKey]);

    if (index === -1) {
      // Prevent further selection if the max selection limit is reached
      if (this.selectedOptions.length >= this.maxSelection) {
        return;
      }
      this.selectedOptions.push(option);
    } else {
      this.selectedOptions.splice(index, 1);
    }

    // Store the names of the selected options
    const selectedNames = this.selectedOptions.map(item => item[this.displayKey]);

    // Set internal change flag to prevent recursion
    this.isInternalChange = true;
    try {
      // Update form control
      if (this.formControl) {
        this.formControl.setValue(selectedNames, { emitEvent: false });
        this.formControl.markAsTouched();
      }

      // Notify Angular about the change through ControlValueAccessor
      this.onChange(selectedNames);
      this.onTouched();

      // Emit selection change event with the complete objects
      this.selectionChange.emit(this.selectedOptions);
    } finally {
      this.isInternalChange = false;
    }

  }

  removeSelectedOption(option: Record<string, any>): void {
    if (this.disabled) return;

    const index = this.selectedOptions.findIndex(selected => selected[this.valueKey] === option[this.valueKey]);
    if (index !== -1) {
      this.selectedOptions.splice(index, 1);
      // Store the names of the selected options
      const selectedNames = this.selectedOptions.map(item => item[this.displayKey]);

      // Set internal change flag to prevent recursion
      this.isInternalChange = true;
      try {
        // Update form control
        if (this.formControl) {
          this.formControl.setValue(selectedNames, { emitEvent: false });
          this.formControl.markAsTouched();
        }

        // Notify Angular about the change through ControlValueAccessor
        this.onChange(selectedNames);
        this.onTouched();

        // Emit selection change event with the complete objects
        this.selectionChange.emit(this.selectedOptions);
      } finally {
        this.isInternalChange = false;
      }
    }
  }

  isSelected(option: Record<string, any>): boolean {
    return Array.isArray(this.selectedOptions) && this.selectedOptions.some(selected => selected[this.valueKey] === option[this.valueKey]);
  }

  private addClickOutsideListener() {
    this.clickListener = this.renderer.listen('document', 'click', (event: MouseEvent) => {
      const targetElement = event.target as HTMLElement;
      if (targetElement && !this.elRef.nativeElement.contains(targetElement)) {
        this.closeDropdown();
      }
    });
  }

  private removeClickOutsideListener() {
    if (this.clickListener) {
      this.clickListener();
      this.clickListener = null;
    }
  }

  get hasValue(): boolean {
    // Check if there are any selected options
    return Array.isArray(this.selectedOptions) && this.selectedOptions.length > 0;
  }
}
